/**
 * SidebarNavGroup — collapsible parent-with-children sidebar row.
 *
 * Generalizes the Spotlight sub-group pattern from PR (m.6) Phase 2.5
 * so any product with 2+ sub-pages renders the same shape:
 *
 *   • Parent row (a Link, not a button) — clicking it navigates to
 *     the product's default sub-page. Chevron sits at the right edge
 *     and is its own click target that toggles expansion (Option B
 *     click semantics — confirmed Phase 2.6).
 *   • Children — when expanded, render as nested rows indented one
 *     level deeper. Active sub-page highlights as in any nav row.
 *
 * Auto-expansion: when the active route is a child of this group,
 * the group is expanded automatically (the user doesn't have to
 * click twice to see where they are).
 *
 * Collapsed sidebar: when the sidebar is rendered in 80px-wide
 * collapsed form (`renderCollapsed`), the chevron is hidden and
 * sub-pages do NOT render — the collapsed rail is top-level
 * products only. In-page `<PillTabs>` handle sub-page selection
 * inside the route. (PR (o) — collapsed-rail simplification.)
 *
 * Spec: meridian-redesign — PR (m.6) Phase 2.6, refined in PR (o)
 * Requirements: 8.2, 8.3, 14.4, 18.5
 */

import { type JSX } from 'react';
import { Link, useNavigate, type Location } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { renderNavRow, type SidebarNavRowItem } from './sidebar-nav-row';

export interface SidebarNavGroupProps {
  /** Group display name. Rendered with `text-nav-item`. */
  name: string;
  /** Lucide icon component for the parent row. */
  icon: React.ComponentType<{ className?: string }>;
  /** Default destination — clicking the parent row goes here. */
  defaultPath: string;
  /** URL prefix that identifies "this group is active". */
  activePathPrefix: string;
  /** Sub-page items rendered when expanded. */
  children: ReadonlyArray<SidebarNavRowItem>;
  /** Current location (passed in to keep this component pure). */
  location: Location;
  /** Whether the parent sidebar is rendering in collapsed (icon-only) form. */
  renderCollapsed: boolean;
  /** Stable test id factory for properties tests. */
  testIdPrefix?: string;
  /**
   * Optional indicator dot rendered on the parent row's icon. Used
   * for unread / pending counts (Podium unread, Approvals pending,
   * etc.) — same red 8x8 dot the flat nav rows use.
   */
  showIndicatorDot?: boolean;
  /**
   * Controlled-open state — the parent sidebar owns the
   * "exactly one group expanded at a time" invariant. When this
   * prop is true the group renders its children expanded; when
   * false they collapse. The chevron toggle calls `onToggle`
   * instead of mutating internal state.
   */
  isOpen: boolean;
  /**
   * Called when the user clicks the chevron toggle. The parent
   * sidebar interprets this as "the user wants this group open"
   * (or, if it was already the open group, "close it").
   */
  onToggle: () => void;
  /**
   * Optional click handler fired when any nav link in this group is
   * tapped. Used by the mobile drawer to close immediately.
   */
  onNavClick?: () => void;
}

export function SidebarNavGroup({
  name,
  icon: Icon,
  defaultPath,
  activePathPrefix,
  children,
  location,
  renderCollapsed,
  testIdPrefix,
  showIndicatorDot,
  isOpen,
  onToggle,
  onNavClick,
}: SidebarNavGroupProps): JSX.Element {
  const isGroupActive = location.pathname.startsWith(activePathPrefix);

  // Parent row keeps the gold treatment whenever any route inside
  // the group is active — both the parent's exact path AND any
  // child sub-page. The child's softer `bg-muted` highlight (see
  // `renderNavRow` nested branch) layers on top, so the user can
  // see "I am inside this product" from the gold parent and "and
  // specifically on this sub-page" from the muted child.
  // Requirements 5.1, 5.2, 5.3 (contestant-admin-ui-polish).
  const isParentHighlighted = isGroupActive;

  // PR (m.6) Phase 2.6.1: open state is now fully controlled by the
  // parent sidebar (it owns the "exactly one group open at a time"
  // invariant). The previous internal useState + auto-expand-on-
  // active behavior moved to the parent's `openGroupName` reducer.
  const open = isOpen;

  // contestant-admin-ui-polish Requirements 18.1, 18.2, 18.3:
  // when the group is currently expanded and the user clicks the
  // parent link, the group SHALL collapse AND navigation SHALL
  // proceed to the first subpage. When collapsed, clicking the
  // parent SHALL expand the group (the existing chevron behavior
  // also fires here so a single click does both — toggle + navigate
  // — without requiring a separate chevron tap).
  //
  // Implementation note: we keep the Link's `to` prop pinned to
  // `defaultPath` so the rendered href (and middle-click /
  // open-in-new-tab semantics) continue to point at the parent's
  // landing route — preserving the structural snapshot asserted by
  // Property 5 in `AdminSidebar.navSuperset.property.test.tsx`.
  // When the group is open the click handler intercepts the
  // navigation and routes to `children[0].path` programmatically
  // instead.
  const navigate = useNavigate();

  return (
    <div className="space-y-1" data-testid={testIdPrefix}>
      <div
        className={cn(
          'flex items-center gap-2 transition-all duration-300 group relative rounded-lg',
          renderCollapsed ? 'justify-center' : '',
          isParentHighlighted
            ? 'bg-primary text-primary-foreground shadow-raised translate-x-1'
            : 'text-foreground hover:bg-muted',
        )}
      >
        <Link
          // contestant-admin-ui-polish Requirements 18.1, 18.2, 18.3:
          // The parent Link's `to` prop is pinned to `defaultPath`
          // (the parent's landing route). When the group is currently
          // expanded the click handler pre-empts the default
          // navigation and routes programmatically to the first
          // subpage instead, satisfying Requirement 18.2 ("navigate
          // to the first subpage within that Nav_Group"). Keeping
          // the rendered href stable preserves middle-click /
          // open-in-new-tab semantics (those continue to surface the
          // parent landing page) and keeps the structural snapshot
          // asserted by Property 5 in
          // `AdminSidebar.navSuperset.property.test.tsx` stable —
          // the parent group's anchor href stays at the canonical
          // pre-redesign path.
          to={defaultPath}
          onClick={(e) => {
            // Group currently expanded: Requirement 18.1 (collapse) +
            // 18.2 (navigate to first subpage). Collapse fires via
            // onToggle below; here we redirect navigation to
            // `children[0].path` so the user lands on the first
            // subpage in a single click.
            if (open && children.length > 0) {
              e.preventDefault();
              navigate(children[0].path);
            }
            // Both branches: toggle expansion. When closed this
            // expands (Requirement 18.3) and the default Link
            // navigation to defaultPath proceeds; when open this
            // collapses (Requirement 18.1).
            onToggle();
            onNavClick?.();
          }}
          data-testid={testIdPrefix ? `${testIdPrefix}-link` : undefined}
          className={cn(
            'flex items-center gap-4 px-4 py-3 min-h-[var(--touch-target-min)] flex-grow min-w-0 rounded-lg group',
            renderCollapsed ? 'justify-center' : '',
          )}
        >
          <Icon
            className={cn(
              'w-4 h-4 shrink-0',
              isParentHighlighted
                ? 'text-primary-foreground'
                : 'text-muted-foreground group-hover:text-foreground',
            )}
          />
          {showIndicatorDot && (
            <div className="absolute left-7 top-2 w-2 h-2 bg-red-500 rounded-full" />
          )}
          {!renderCollapsed && (
            <span className="text-nav-item flex-grow truncate">{name}</span>
          )}
          {renderCollapsed && (
            <div className="absolute left-full ml-4 px-3 py-2 bg-foreground text-background text-nav-item rounded-sm opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity whitespace-nowrap z-50 shadow-prominent border border-border">
              {name}
            </div>
          )}
        </Link>
        {!renderCollapsed && (
          <button
            type="button"
            aria-expanded={open}
            aria-controls={`${testIdPrefix ?? name.toLowerCase()}-children`}
            data-testid={testIdPrefix ? `${testIdPrefix}-toggle` : undefined}
            onClick={(e) => {
              e.stopPropagation();
              onToggle();
            }}
            className={cn(
              'p-2 mr-1 shrink-0 rounded-md transition-colors touch-target',
              'bg-transparent border-none cursor-pointer',
              isParentHighlighted
                ? 'text-primary-foreground/80 hover:bg-primary-foreground/10'
                : 'text-muted-foreground hover:bg-muted',
            )}
          >
            <ChevronDown
              className={cn(
                'w-3 h-3 transition-transform',
                open ? '' : '-rotate-90',
              )}
            />
          </button>
        )}
      </div>

      {/* Children — expanded form when the sidebar is wide */}
      {open && !renderCollapsed && (
        <div
          id={`${testIdPrefix ?? name.toLowerCase()}-children`}
          className="ml-3 pl-3 border-l border-border space-y-1"
        >
          {children.map((child, idx) =>
            renderNavRow(child, {
              location,
              renderCollapsed,
              nested: true,
              // contestant-admin-ui-polish task 16.1: when the user
              // sits on the parent route with no `?tab=` selected,
              // the page renders its first tab by default — flag the
              // first child so `renderNavRow` highlights it as the
              // current subpage. Other children fall through to the
              // strict `isPathActive` match.
              isDefaultForGroup: idx === 0,
              onNavClick,
            }),
          )}
        </div>
      )}

      {/*
        Collapsed sidebar: do NOT render sub-pages. The collapsed
        rail shows top-level products only; in-page <PillTabs>
        handle sub-page selection. PR (o) — Collapsed sidebar
        shows top-level only.
      */}
    </div>
  );
}

export default SidebarNavGroup;
